import { CommonModule } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { NgModule, Optional, SkipSelf } from '@angular/core';
import { RouteReuseStrategy, RouterModule } from '@angular/router';
import { BASE_PATH as TENANTS_BASE_PATH } from '@app/clients/tenants/gen';
import { ContextService } from '@app/core/context.service';
import { WindowProvider } from '@app/core/window.service';
import { Config, ConfigProvider, ConfigToken } from '@dpod/gem-ui-common-ng';
import { environment } from '@env/environment';
import { TranslateModule } from '@ngx-translate/core';
import { RecaptchaModule, RecaptchaSettings, RECAPTCHA_SETTINGS } from 'ng-recaptcha';
import { ApiModule as TenantsApiModule } from '../clients/tenants/gen/api.module';
import { ApiPrefixInterceptor } from './http/api-prefix.interceptor';
import { CacheInterceptor } from './http/cache.interceptor';
import { ErrorHandlerInterceptor } from './http/error-handler.interceptor';
import { HttpCacheService } from './http/http-cache.service';
import { HttpService } from './http/http.service';
import { I18nService } from './i18n.service';
import { RouteReusableStrategy } from './route-reusable-strategy';

/**
 * Override the base path to prevent the strange `localhost` URL in the DPOD OpenAPI spec
 * from being used.
 */
const basePath = '';

@NgModule({
  imports: [
    CommonModule,
    HttpClientModule,
    TranslateModule,
    RecaptchaModule,
    RouterModule,
    TenantsApiModule,
  ],
  providers: [
    ConfigProvider,
    I18nService,
    HttpCacheService,
    ApiPrefixInterceptor,
    ErrorHandlerInterceptor,
    CacheInterceptor,
    ContextService,
    WindowProvider,
    {
      provide: HttpClient,
      useClass: HttpService
    },
    {
      provide: RouteReuseStrategy,
      useClass: RouteReusableStrategy
    },
    {
      provide: TENANTS_BASE_PATH,
      useValue: basePath,
    },
    (
      // Use site key if one is provided in the runtime config (for production),
      // otherwise fallback to the value hardcoded in the environment (for local dev)
      {
        provide: RECAPTCHA_SETTINGS,
        deps: [ConfigToken],
        useFactory: (config: Config): RecaptchaSettings => {
          return { siteKey: config.RECAPTCHA_SITE_KEY || environment.recaptchaSiteKey };
        },
      }
    ),
  ]
})
export class CoreModule {

  constructor(@Optional() @SkipSelf() parentModule: CoreModule) {
    // Import guard
    if (parentModule) {
      throw new Error(`${parentModule} has already been loaded. Import Core module in the AppModule only.`);
    }
  }

}
